import type { PlanType } from '@vidly/types';

export interface PlanConfig {
  name: string;
  priceMonthlyEur: number;
  maxDailyJobs: number;
  maxOriginalDurationSeconds: number; // e.g. 15 min or 60 min
  maxOutputDurationSeconds: number;   // e.g. 10 min or 30 min
  maxConcurrentJobs: number;
  maxUploadSizeBytes: number;        // 1GB vs 5GB
  maxResolution: string;             // 1080p
  retentionHours: number;            // 24h vs 720h (30 days)
  adsEnabled: boolean;
  priorityQueue: boolean;
  description: string;
  features: string[];
}

export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
  free: {
    name: 'Free',
    priceMonthlyEur: 0,
    maxDailyJobs: 1,
    maxOriginalDurationSeconds: 15 * 60, // 15 mins
    maxOutputDurationSeconds: 10 * 60,   // 10 mins
    maxConcurrentJobs: 1,
    maxUploadSizeBytes: 1 * 1024 * 1024 * 1024, // 1 GB
    maxResolution: '1080p',
    retentionHours: 24, // 24 hours auto-delete
    adsEnabled: true,
    priorityQueue: false,
    description: 'Perfecto para probar la potencia de resúmenes de vídeo con IA.',
    features: [
      '1 vídeo resumido al día',
      'Hasta 15 minutos de vídeo original',
      'Hasta 10 minutos de vídeo resultante',
      'Resolución hasta 1080p',
      '1 trabajo activo a la vez',
      'Eliminación automática en 24 horas',
      'Anuncios display discretos'
    ]
  },
  premium: {
    name: 'Premium',
    priceMonthlyEur: 14.99,
    maxDailyJobs: 5,
    maxOriginalDurationSeconds: 60 * 60, // 60 mins
    maxOutputDurationSeconds: 30 * 60,   // 30 mins
    maxConcurrentJobs: 2,
    maxUploadSizeBytes: 5 * 1024 * 1024 * 1024, // 5 GB
    maxResolution: '1080p',
    retentionHours: 30 * 24, // 30 days retention
    adsEnabled: false,
    priorityQueue: true,
    description: 'Para creadores de contenido, podcasters y profesionales con necesidades diarias.',
    features: [
      '5 vídeos resumidos al día',
      'Hasta 60 minutos de vídeo original',
      'Hasta 30 minutos de vídeo resultante',
      'Resolución 1080p sin limitaciones',
      '2 trabajos simultáneos concurrentes',
      'Cola de renderizado de alta prioridad',
      'Almacenamiento e historial de 30 días',
      '100% libre de publicidad',
      'Soporte prioritario por email'
    ]
  }
};

export const VIDEO_LIMITS = {
  allowedContainers: ['mp4', 'mov', 'm4v'],
  allowedVideoCodecs: ['h264', 'avc1', 'h265', 'hevc'],
  allowedAudioCodecs: ['aac', 'mp3', 'opus'],
  maxFps: 60,
  maxWidth: 1920,
  maxHeight: 1080,
  minSegmentDurationSeconds: 2, // Segments under 2 seconds are filtered out for smoothness
  maxSegmentDurationSeconds: 600,
  r2PresignedUploadExpireSeconds: 3600, // 1 hour to complete upload
  r2PresignedDownloadExpireSeconds: 7200 // 2 hours
};

export const AI_CONFIG = {
  primaryProvider: 'gemini',
  fallbackProvider: 'openrouter',
  geminiModel: 'gemini-2.5-flash',
  openRouterModel: 'anthropic/claude-3.5-sonnet',
  maxRetries: 3,
  retryDelayMs: 1500,
  requestTimeoutMs: 45000,
  // System prompt guidelines
  minConfidenceThreshold: 0.7
};

export const PROCESSOR_CONFIG = {
  maxProcessingTimeSeconds: 600, // 10 minutes hard timeout per job
  ffmpegPresets: {
    videoCodec: 'libx264',
    crf: 22,
    preset: 'veryfast',
    audioCodec: 'aac',
    audioBitrate: '192k'
  }
};
